import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import { useTranslation } from 'react-i18next'

const TILE_SIZE = 70

const ICONS = {
  students: GroupsOutlinedIcon,
  staff: BadgeOutlinedIcon,
  parents: FamilyRestroomOutlinedIcon,
  applications: DescriptionOutlinedIcon,
  attendance: EventAvailableOutlinedIcon,
  school: SchoolOutlinedIcon,
  multiSchool: AccountTreeOutlinedIcon,
}

const TONES = {
  students: 'info',
  staff: 'warning',
  parents: 'secondary',
  applications: 'primary',
  attendance: 'success',
  school: 'success',
  multiSchool: 'warning',
}

export default function MetricCard({ card, format }) {
  const { t } = useTranslation()

  const Icon = ICONS[card.icon] ?? InsightsOutlinedIcon
  const tone = TONES[card.icon] ?? 'primary'
  const delta = card.delta === null || card.delta === undefined ? null : Number(card.delta)
  const rising = delta !== null && delta >= 0

  return (
    <>
      <Icon
        sx={{
          position: 'absolute',
          right: -26,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 132,
          color: theme => alpha(theme.palette[tone].main, theme.palette.mode === 'dark' ? 0.1 : 0.07),
          pointerEvents: 'none',
        }}
      />

      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', minWidth: 0, zIndex: 1 }}>
        <Box
          sx={{
            width: TILE_SIZE,
            height: TILE_SIZE,
            flexShrink: 0,
            borderRadius: 3,
            display: 'grid',
            placeItems: 'center',
            color: theme => theme.palette[tone].contrastText ?? '#fff',
            backgroundImage: theme =>
              `linear-gradient(135deg, ${theme.palette[tone].light}, ${theme.palette[tone].dark})`,
            boxShadow: theme => `0 10px 20px -6px ${alpha(theme.palette[tone].main, 0.6)}`,
          }}
        >
          <Icon sx={{ fontSize: 40 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', lineHeight: 1.4 }}>
            {t(card.label ?? '')}
          </Typography>
          <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-.01em' }}>
            {format(card.value)}
            {card.unit && (
              <Box component="span" sx={{ fontSize: 18, fontWeight: 700 }}>
                {card.unit}
              </Box>
            )}
          </Typography>
        </Box>

        {delta !== null && (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.25} justifyContent="flex-end">
              {rising ? (
                <ArrowUpwardIcon sx={{ fontSize: 18, color: 'success.main' }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 18, color: 'error.main' }} />
              )}
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: rising ? 'success.main' : 'error.main' }}>
                {Math.abs(delta)}%
              </Typography>
            </Stack>
            {card.caption && (
              <Typography sx={{ fontSize: 13, color: 'text.disabled' }} noWrap>
                {t(card.caption)}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </>
  )
}
